import {FC} from 'react';
import {
	DocumentPhotoSlotValue,
	DocumentsSlotConfig,
	ForeignDocumentDetailsStepValues,
	ForeignDocumentFieldConfig,
	getBirthDateError,
	getDateExistsError,
	getForeignDocumentFormConfig,
	getIssuedAtDateError,
} from '../../model';
import {StepLayout} from 'features/registration';
import {DocumentTypeSelect} from '../components/DocumentTypeSelect/DocumentTypeSelect';
import {PassportPhotoMultiUpload} from '../components/PassportPhotoUpload/PassportPhotoMultiUpload';
import {ForeignDocumentFieldControl} from './ForeignDocumentFieldControl';
import {resolveForeignDocumentFieldControl} from './resolveForeignDocumentFieldControl';
import styles from './ForeignDocumentDetailsStep.module.css';

type Props = {
	values: ForeignDocumentDetailsStepValues;
	onChange: (patch: Partial<ForeignDocumentDetailsStepValues>) => void;
	citizenshipOksmCode?: string | null;
	residenceOksmCode?: string | null;
	title?: string;
	/** Слоты фото на этом же экране — как у РФ. */
	photoSlots?: DocumentsSlotConfig[];
	photoGroupLabel?: string;
	photoValues?: Record<string, DocumentPhotoSlotValue>;
	onPhotoSelect?: (slotId: string, file: File) => void;
	onPhotoRemove?: (slotId: string) => void;
};

const getFieldError = (
	field: ForeignDocumentFieldConfig,
	values: ForeignDocumentDetailsStepValues,
): string | null => {
	if (field.input !== 'date') {
		return null;
	}

	if (field.key === 'birthDate') {
		return getBirthDateError(values.birthDate);
	}

	if (field.key === 'issuedAt') {
		return getIssuedAtDateError(values.issuedAt);
	}

	return getDateExistsError(values.validTill);
};

export const ForeignDocumentDetailsStep: FC<Props> = ({
	values,
	onChange,
	citizenshipOksmCode,
	residenceOksmCode,
	title,
	photoSlots,
	photoGroupLabel = 'Фото паспорта',
	photoValues,
	onPhotoSelect,
	onPhotoRemove,
}) => {
	const config = getForeignDocumentFormConfig(
		citizenshipOksmCode,
		values.documentType,
		residenceOksmCode,
	);
	const resolvedTitle = title ?? config.title;
	const showPhotos = Boolean(photoSlots?.length && onPhotoSelect && onPhotoRemove);

	return (
		<StepLayout>
			<StepLayout.Title>{resolvedTitle}</StepLayout.Title>

			<StepLayout.Content className={styles.fields}>
				{config.documentTypeOptions ? (
					<DocumentTypeSelect
						value={values.documentType}
						options={config.documentTypeOptions}
						onChange={(documentType) => onChange({documentType})}
					/>
				) : null}

				{showPhotos ? (
					<div className={styles.photoBlock}>
						<span className={styles.photoGroupLabel}>{photoGroupLabel}</span>
						<PassportPhotoMultiUpload
							items={photoSlots!.map((slot) => ({
								id: slot.id,
								label: slot.label,
								previewUrl: photoValues?.[slot.id]?.previewUrl ?? null,
							}))}
							onSelect={onPhotoSelect!}
							onRemove={onPhotoRemove!}
						/>
					</div>
				) : null}

				{config.fields.map((field) => {
					const control = resolveForeignDocumentFieldControl(field, values);

					return (
						<ForeignDocumentFieldControl
							key={field.key}
							label={field.label}
							error={getFieldError(field, values)}
							name={control.name}
							value={control.value}
							placeholder={control.placeholder}
							inputMode={control.inputMode}
							maxLength={control.maxLength}
							multiline={control.multiline}
							autoComplete={control.autoComplete}
							prefix={control.prefix}
							ariaLabel={control.ariaLabel}
							onValueChange={(raw) =>
								onChange({[field.key]: control.normalize(raw)} as Partial<ForeignDocumentDetailsStepValues>)
							}
							onClear={() =>
								onChange({
									[field.key]: control.clearValue,
								} as Partial<ForeignDocumentDetailsStepValues>)
							}
						/>
					);
				})}
			</StepLayout.Content>
		</StepLayout>
	);
};
